import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, MessageSquare, Volume2, Sparkles } from 'lucide-react';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { ScrollReveal } from '../../components/ScrollReveal';
import { CAALiveDemo } from '../../components/CAALiveDemo';
import { CreateCategoryCard } from '../../components/CreateCategoryCard';

// Nomes e cores das categorias reais do app (colors.xml + strings.xml —
// alfabeto_category, numeros, familia, saudacoes, necess, saude, roupas,
// alimentos, lugares, animais). Nenhuma inventada.
const categorias = [
  { name: 'Alfabeto', color: '#03A9F4' },
  { name: 'Números', color: '#FF3C62' },
  { name: 'Família', color: '#76EFB1' },
  { name: 'Saudações', color: '#FF7C19' },
  { name: 'Necessidades', color: '#FF5CA7' },
  { name: 'Saúde', color: '#F32E2E' },
  { name: 'Roupas', color: '#FFDD55' },
  { name: 'Alimentos', color: '#BF8EEF' },
  { name: 'Lugares', color: '#A9D4FB' },
  { name: 'Animais', color: '#00ED83' },
];

const ComunicacaoPage = () => {
  return (
    <div className="min-h-screen bg-background">
      <Header />

      {/* Hero */}
      <section className="relative pt-16 pb-14 md:pt-20 md:pb-16 bg-background overflow-hidden">
        <div
          className="absolute inset-0 pointer-events-none opacity-[0.35] dark:opacity-[0.12]"
          style={{ backgroundImage: 'radial-gradient(rgb(var(--primary)) 1px, transparent 1px)', backgroundSize: '26px 26px' }}
          aria-hidden="true"
        />
        <div
          className="absolute -top-24 -left-24 w-96 h-96 rounded-full opacity-25 blur-3xl pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgb(var(--primary)) 0%, transparent 70%)' }}
          aria-hidden="true"
        />

        <div className="relative container mx-auto px-4">
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors mb-6">
            <ArrowLeft className="w-4 h-4" />
            Início
          </Link>

          <div className="inline-flex items-center gap-2 bg-primary/10 dark:bg-primary/15 px-3 py-1.5 rounded-full text-sm font-semibold text-foreground mb-5">
            <MessageSquare className="w-4 h-4 text-primary-ink dark:text-primary-tint" />
            Comunicação Alternativa (CAA)
          </div>

          <h1 className="text-4xl md:text-6xl font-bold text-foreground max-w-3xl leading-[1.1]">
            O Conectar fala por cada criança
          </h1>
          <p className="font-body text-lg md:text-xl text-muted-foreground mt-4 max-w-2xl">
            Cartões visuais organizados por categoria, com voz natural ao toque — uma forma
            direta de se expressar, sem depender da fala oral.
          </p>
        </div>
      </section>

      {/* Demo ao vivo — cartão grande em destaque, igual ao card de comunicação da home */}
      <ScrollReveal>
        <section className="py-16 md:py-20 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto rounded-[2.5rem] bg-gradient-to-br from-primary-tint via-primary to-primary-ink text-white p-8 md:p-12 grid md:grid-cols-2 gap-10 items-center overflow-hidden relative">
              <div
                className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-white/20 blur-3xl pointer-events-none"
                aria-hidden="true"
              />
              <div className="relative">
                <h2 className="text-2xl md:text-3xl font-bold leading-snug mb-4">
                  O Conectar{' '}
                  <span className="inline-flex items-center gap-1.5 bg-white/20 rounded-full px-3 py-1 text-xl align-middle">
                    <Volume2 className="w-4 h-4" />
                    fala
                  </span>{' '}
                  por ela
                </h2>
                <p className="text-white/85 text-base leading-relaxed">
                  Toque num cartão e a frase associada é sintetizada em voz e reproduzida na
                  hora, em português — dando à criança uma forma imediata de se expressar.
                </p>
              </div>
              <div className="relative w-full max-w-xs mx-auto">
                <CAALiveDemo />
              </div>
            </div>
          </div>
        </section>
      </ScrollReveal>

      {/* Categorias reais do app */}
      <ScrollReveal>
        <section className="py-16 md:py-20 bg-background">
          <div className="container mx-auto px-4">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground text-center mb-2">
              10 categorias sempre à mão
            </h2>
            <p className="text-muted-foreground text-center max-w-xl mx-auto mb-10">
              Cada categoria tem sua própria cor, pensada pra facilitar o reconhecimento
              visual rápido.
            </p>
            <div className="flex flex-wrap justify-center gap-3 max-w-3xl mx-auto">
              {categorias.map((cat) => (
                <span
                  key={cat.name}
                  className="inline-flex items-center gap-2.5 rounded-full px-5 py-3 text-sm font-semibold text-foreground"
                  style={{ backgroundColor: `${cat.color}1a` }}
                >
                  <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: cat.color }} />
                  {cat.name}
                </span>
              ))}
            </div>
          </div>
        </section>
      </ScrollReveal>

      {/* Criar categoria própria */}
      <ScrollReveal>
        <section className="py-16 md:py-20 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto grid md:grid-cols-2 gap-10 items-center">
              <div>
                <div className="inline-flex items-center gap-2 bg-warm/10 dark:bg-warm/15 px-3 py-1.5 rounded-full text-sm font-semibold text-foreground mb-5">
                  <Sparkles className="w-4 h-4 text-warm-ink dark:text-warm" />
                  Liberdade para criar
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-4">
                  Além das categorias prontas, você cria as suas
                </h2>
                <p className="text-base text-muted-foreground leading-relaxed">
                  Cartões e categorias totalmente personalizáveis: cada família pode criar os
                  seus com foto e texto próprios — dentro de uma categoria existente ou de uma
                  rotina.
                </p>
              </div>
              <div className="w-full max-w-xs mx-auto">
                <CreateCategoryCard />
              </div>
            </div>
          </div>
        </section>
      </ScrollReveal>

      {/* CTA final */}
      <section className="py-16 bg-primary/5">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-4">
            Quer ver os outros recursos?
          </h2>
          <Link
            to="/recursos/desenho-ia"
            className="inline-flex items-center gap-1.5 font-sans font-bold text-[15px] px-7 py-3.5 rounded-full bg-primary text-white hover:bg-primary/90 transition-transform hover:-translate-y-0.5"
          >
            Conhecer o Desenho com IA
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ComunicacaoPage;
